import {
	Button,
	createStyles,
	Input,
	makeStyles,
	Theme,
} from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import React from "react";
import {
	DATA_TYPES,
	FILTER_OPS_CAT,
	FILTER_OPS_NUM,
	FONTS,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
} from "../../constants";
import { batchDataType } from "../../helpers/processBatchData";
import { stateType, stateTypeF } from "../../state";
import { validateFilters } from "../DataCleaning/FilterButton";
import { applyFilter } from "./applyFilter";

const useStyles = makeStyles((theme: Theme) => {
	return createStyles({
		filterButtonContainer: {
			marginTop: "25px",
			marginRight: "40px",
			marginLeft: "auto",
			cursor: "pointer",
		},
		filterButton: { "&:hover": { transform: "scale(1.05)" } },
		dropDown: {
			width: "140px",
			height: "30px",
			border: "solid",
			marginLeft: "20px",
			borderRadius: "5px",
			borderColor: "rgba(0,0,0,0.2)",
			borderWidth: "2px",
			marginTop: "-5px",
			display: "flex",
			flexDirection: "row",
			transition: "0.3s",
			cursor: "pointer",
			backgroundColor: "white",
			"&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
		},
		formControl: {
			margin: theme.spacing(1),
			minWidth: 120,
		},
		filterRow: { display: "flex", flexDirection: "row" },
		selectUnderline: {
			"&:before": {
				borderBottom: `1px solid ${SECONDARY_COLOR}`,
			},
			"&:after": {
				borderBottom: `2px solid ${SECONDARY_COLOR}`,
			},
			//   "&:hover:not($disabled):not($focused):not($error):before": {
			//     borderBottom: `1px solid ${SECONDARY_COLOR}`,
			//   },
			"&:hover:before": {
				borderBottom: `1px solid ${SECONDARY_COLOR}`,
			},
		},
		floatingLabelFocusStyle: {
			"&.Mui-focused": {
				color: PRIMARY_COLOR,
			},
			color: PRIMARY_COLOR,
			fontFamily: FONTS,
		},
		textField: {
			"& .MuiInputBase-input": {
				color: PRIMARY_COLOR, // Text color
			},
			"& .MuiInput-underline:before": {
				borderBottomColor: SECONDARY_COLOR, // Semi-transparent underline
			},
			"& .MuiInput-underline:hover:before": {
				borderBottomColor: SECONDARY_COLOR, // Solid underline on hover
			},
			"& .MuiInput-underline:after": {
				borderBottomColor: SECONDARY_COLOR, // Solid underline on focus
			},
		},
		textFieldInvalid: {
			"& .MuiInputBase-input": {
				color: PRIMARY_COLOR, // Text color
			},
			"& .MuiInput-underline:before": {
				borderBottomColor: "red", // Semi-transparent underline
			},
			"& .MuiInput-underline:hover:before": {
				borderBottomColor: "red", // Solid underline on hover
			},
			"& .MuiInput-underline:after": {
				borderBottomColor: "red", // Solid underline on focus
			},
		},
		inputLabel: {
			color: PRIMARY_COLOR,
			fontFamily: FONTS,
			"&.Mui-focused": { color: PRIMARY_COLOR },
		},
		closeButton: {
			marginTop: "34px",
			marginLeft: "15px",
			marginRight: "5px",
			height: "17px",
			width: "17px",
			cursor: "pointer",
			"&:hover": { transform: "scale(1.1)" },
		},
	});
});

type filterInputType = { state: stateTypeF; index: number };

function getColType(column: string, batchData: batchDataType) {
	let ndx = batchData.headers.indexOf(column);
	if (typeof batchData.data[0][ndx] !== "string") {
		return DATA_TYPES.number;
	} else {
		return DATA_TYPES.categorical;
	}
}

function ColDropDown({ state, index }: filterInputType) {
	const classes = useStyles();

	function handleChange(e) {
		let filters = state.batchFilters;
		const val = e.target.value;

		let prevType = getColType(
			filters[index].column,
			state.saveState.batchPrediction.batchData
		);
		let nextType = getColType(
			val,
			state.saveState.batchPrediction.batchData
		);
		if (prevType !== nextType) {
			if (nextType === DATA_TYPES.number) {
				filters[index].operation = FILTER_OPS_NUM.equal;
			} else {
				filters[index].operation = FILTER_OPS_CAT.equal;
			}
		}
		filters[index].column = val;
		filters[index].value = "";

		state.setBatchFilters(filters);
	}

	let values = state.saveState.batchPrediction.batchData.headers;
	let usableCols = new Set(
		state.saveState.dataset.columns
			.filter((col) => col.columnType !== DATA_TYPES.unusable)
			.map((col) => col.columnName)
	);
	values = values.filter((val) => usableCols.has(val));

	return (
		<FormControl className={classes.formControl}>
			<InputLabel
				id="demo-customized-select-label"
				className={classes.inputLabel}
				shrink={true}
			>
				{"Column"}
			</InputLabel>
			<Select
				labelId="demo-customized-select-label"
				id="demo-customized-select"
				value={state.batchFilters[index].column}
				onChange={handleChange}
				style={{ width: "150px", fontFamily: FONTS }}
				input={
					<Input
						classes={{
							underline: classes.selectUnderline,
						}}
					/>
				}
			>
				{values.map((val2) => (
					<MenuItem value={val2}>{val2}</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}

function OpDropDown({ state, index }: filterInputType) {
	const classes = useStyles();

	function handleChange(e) {
		let filters = state.batchFilters;
		const val = e.target.value;
		filters[index] = {
			column: filters[index].column,
			operation: val,
			value: filters[index].value,
			isValid: true,
		};
		state.setBatchFilters(filters);
	}

	let filter2 = state.batchFilters[index];

	let values = Object.values(FILTER_OPS_CAT);
	if (
		getColType(
			filter2.column,
			state.saveState.batchPrediction.batchData
		) === DATA_TYPES.number
	) {
		values = Object.values(FILTER_OPS_NUM);
	}

	return (
		<FormControl className={classes.formControl}>
			<InputLabel
				id="demo-customized-select-label"
				className={classes.inputLabel}
				shrink={true}
			>
				{"Operation"}
			</InputLabel>
			<Select
				labelId="demo-customized-select-label"
				id="demo-customized-select"
				value={state.batchFilters[index].operation}
				onChange={handleChange}
				style={{ width: "130px", fontFamily: FONTS }}
				input={
					<Input
						classes={{
							underline: classes.selectUnderline,
						}}
					/>
				}
			>
				{values.map((val2) => (
					<MenuItem value={val2}>{val2}</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}

function ValDropDown({ state, index }: filterInputType) {
	const classes = useStyles();

	function handleChange(e) {
		let filters = state.batchFilters;
		const val = e.target.value;
		filters[index].value = val;
		state.setBatchFilters(filters);
	}

	let filter = state.batchFilters[index];

	let values = [];
	if (
		filter.column ===
		`Predicted: ${state.saveState.modelTraining.valueToPredict}`
	) {
		values = state.saveState.dataset.getColumn(
			state.saveState.modelTraining.valueToPredict
		).allClasses;
	} else {
		values = state.saveState.dataset.getColumn(filter.column).allClasses;
	}

	return (
		<FormControl className={classes.formControl}>
			<InputLabel
				id="demo-customized-select-label"
				className={classes.inputLabel}
				shrink={true}
			>
				{"Value"}
			</InputLabel>
			<Select
				labelId="demo-customized-select-label"
				id="demo-customized-select"
				value={state.batchFilters[index].value}
				onChange={handleChange}
				style={{
					width: "150px",
					marginRight: "20px",
					fontFamily: FONTS,
				}}
				input={
					<Input
						classes={{
							underline: classes.selectUnderline,
						}}
					/>
				}
			>
				{values.map((val2) => (
					<MenuItem value={val2}>{val2}</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}

type valueSelectInput = { state: stateTypeF; index: number };

function NumericFilterCol({ state, index }: valueSelectInput) {
	const classes = useStyles();
	function handleChange(e) {
		let filter = state.batchFilters[index];
		let val = e.target.value;

		filter.value = val;

		state.batchFilters[index] = filter;
		state.setBatchFilters(state.batchFilters);
	}

	return (
		<TextField
			id="standard-basic"
			label="Value"
			variant="standard"
			style={{
				width: "150px",
				marginTop: "8px",
				marginRight: "20px",
				fontFamily: FONTS,
				marginLeft: "8px",
			}}
			value={state.batchFilters[index].value}
			onChange={handleChange}
			inputProps={{
				maxLength: 25,
			}}
			className={
				state.batchFilters[index].isValid
					? classes.textField
					: classes.textFieldInvalid
			}
			InputLabelProps={{
				className: classes.floatingLabelFocusStyle,
				shrink: true,
			}}
		/>
	);
}

function ValueSelect({ state, index }: valueSelectInput) {
	const column = state.batchFilters[index].column;
	if (
		getColType(column, state.saveState.batchPrediction.batchData) ===
		DATA_TYPES.categorical
	) {
		return <ValDropDown state={state} index={index}></ValDropDown>;
	} else {
		return (
			<NumericFilterCol state={state} index={index}></NumericFilterCol>
		);
	}
}

type filterMenuType = { state: stateTypeF; anchorEl; setAnchorEl };
function FilterMenu({ state, anchorEl, setAnchorEl }: filterMenuType) {
	const classes = useStyles();
	const handleClose = () => {
		setAnchorEl(null);
	};
	function handleApply(e) {
		let { filters, allValid } = validateFilters(
			state.batchFilters,
			state.saveState.dataset
		);
		if (!allValid) {
			state.setBatchFilters(filters);
			return;
		}
		let filterRows = applyFilter(
			state.saveState.batchPrediction.batchData.data,
			state.saveState.batchPrediction.batchData.headers,
			state.batchFilters
		);
		state.batchFilteredData = filterRows;
		state.setSaveState(state.saveState);
		setAnchorEl(null);
	}
	function handleAdd(e) {
		let filters = state.batchFilters;
		if (filters.length >= 5) {
			return;
		}
		filters.push({
			column: "",
			operation: "",
			value: "",
			isValid: true,
		});
		state.setBatchFilters(filters);
	}

	function handleDelete(index: number) {
		let filters = state.batchFilters;
		filters.splice(index, 1);
		state.setBatchFilters(filters);
	}
	return (
		<Menu
			id="simple-menu"
			anchorEl={anchorEl}
			open={Boolean(anchorEl)}
			onClose={handleClose}
			style={{ marginTop: "30px", marginLeft: "-50px" }}
		>
			{state.batchFilters.map((filter, index) => (
				<div className={classes.filterRow}>
					<div
						style={{
							color: PRIMARY_COLOR,
						}}
						onClick={() => handleDelete(index)}
					>
						<CloseIcon className={classes.closeButton}></CloseIcon>
					</div>
					<ColDropDown state={state} index={index}></ColDropDown>
					<OpDropDown state={state} index={index}></OpDropDown>
					<ValueSelect state={state} index={index}></ValueSelect>
				</div>
			))}
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					marginLeft: "30px",
					marginTop: "10px",
					marginBottom: "5px",
					width: "500px",
				}}
			>
				<Button
					onClick={handleAdd}
					style={{ fontFamily: FONTS, fontWeight: "bold" }}
				>
					+ Filter
				</Button>
				<Button
					onClick={handleApply}
					variant={"outlined"}
					style={{
						fontFamily: FONTS,
						fontWeight: "bold",
						marginLeft: "20px",
					}}
				>
					Apply
				</Button>
			</div>
		</Menu>
	);
}

export function FilterButton({ state }: stateType) {
	const classes = useStyles();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	return (
		<div className={classes.filterButtonContainer}>
			<div onClick={(event: any) => handleClick(event)}>
				<FilterListIcon
					className={classes.filterButton}
				></FilterListIcon>
			</div>
			<FilterMenu
				state={state}
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
			></FilterMenu>
		</div>
	);
}
